/**
 * @file index.tsx
 *
 * @fileoverview Renders messages based on the content set to the apollo link state
 * and internal store for holding any kind of shared objects.
 */

import React, { useEffect } from 'react';
import { useStore } from '~/jobiqo/context/state';
import { getMessages, clearMessages } from '~/jobiqo/services/messages';
import { Message } from '~/jobiqo/models/app';
import { MessageList } from './MessageList';
import Router from 'next/router';
import LazyHydrate from 'react-lazy-hydration';
import { Main } from '~/vcl/atoms/06-layout/09-main';

interface MessageProps {
  /**
   * If the message should be displayed full width.
   */
  fullWidth?: boolean;
  /**
   * Main content size.
   * @default 'large'
   */
  size?: 'small' | 'medium' | 'large' | 'xLarge';
  /**
   * If true no margin will be set around messages.
   *
   * @default false
   */
  noMargin?: boolean;
}

const getAppMessages = (): Message[] => {
  const appMessages = getMessages();
  clearMessages();
  return appMessages;
};

export const Messages = ({
  fullWidth = false,
  size = 'large',
  noMargin = false,
}: MessageProps) => {
  // Get the client from app context.
  const { state, dispatch } = useStore();
  // Combine messages sent through routes and context level messages (in state).
  const stateMessages = state && state.messages ? state.messages : [];
  const totalMessages: Message[] = [...stateMessages, ...getAppMessages()];

  useEffect(() => {
    // schedule a cleanup for the next navigation.
    Router.events.on('routeChangeStart', () =>
      dispatch({
        type: 'clearMessages',
      }),
    );
  }, []);

  return (
    <LazyHydrate whenIdle={true}>
      {fullWidth ? (
        <MessageList dispatch={dispatch} messages={totalMessages} />
      ) : (
        <Main
          {...(noMargin ? { margin: 0 } : null)}
          paddingLeft={[0, 0]}
          paddingRight={[0, 0]}
          size={size}
        >
          <MessageList dispatch={dispatch} messages={totalMessages} />
        </Main>
      )}
    </LazyHydrate>
  );
};
export default Messages;
